import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { z } from 'zod';
import { PRO_MIN_PASSWORD_LENGTH } from '@erp/domain/pro';
import { useProAuth } from '@/lib/auth-context';
import { useGovernorateOptions } from '@/lib/governorates';
import { proErrorMessage } from '@/lib/pro-error';
import { resolveProTenantSubdomain } from '@/lib/pro-tenant';
import { AuthLayout } from '@/components/layout/AuthLayout';

// L'électricien choisit le mot de passe qu'il veut : PAS d'exigence de
// majuscule, de minuscule ni de chiffre. Seule reste la longueur minimale
// que Supabase Auth impose de toute façon côté serveur (cf. PRO_MIN_PASSWORD_LENGTH).
const passwordSchema = z
  .string()
  .min(PRO_MIN_PASSWORD_LENGTH, `Au moins ${PRO_MIN_PASSWORD_LENGTH} caractères`);

const formSchema = z.object({
  firstName: z.string().min(1, 'Prénom requis'),
  lastName: z.string().min(1, 'Nom requis'),
  email: z.string().email('Email invalide'),
  phone: z.string().regex(/^[0-9]{8,15}$/, '8 à 15 chiffres'),
  whatsapp: z.string().optional(),
  password: passwordSchema,
});

type FormData = z.infer<typeof formSchema>;

type QuestionsData = {
  experience?: string;
  workType?: string;
  region?: string;
} & Record<string, unknown>;

export function RegisterPage() {
  const { t } = useTranslation();
  const { signupAsPro } = useProAuth();
  const navigate = useNavigate();
  const governorates = useGovernorateOptions();
  const [step, setStep] = useState<'form' | 'questions' | 'submitting'>('form');
  const [form, setForm] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    whatsapp: '',
    password: '',
  });
  const [questions, setQuestions] = useState<QuestionsData>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Hostname-first resolution: tunielecpro.gesty.tn → tenant
  // `tunielec`. Falls back to the build-time env var on preview URLs
  // and localhost. See libs/pro-tenant.ts for the full convention.
  const tenantSubdomain = resolveProTenantSubdomain();

  const onFormNext = (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = formSchema.safeParse(form);
    if (!parsed.success) {
      const fieldErrors: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const path = issue.path[0];
        if (typeof path === 'string') fieldErrors[path] = issue.message;
      }
      setErrors(fieldErrors);
      return;
    }
    setErrors({});
    setStep('questions');
  };

  const onFinalSubmit = async () => {
    if (!tenantSubdomain) {
      setSubmitError(
        t('pro.auth.no_tenant_configured', 'Aucun fournisseur configuré pour cette plateforme.'),
      );
      return;
    }
    setSubmitError(null);
    setStep('submitting');
    try {
      const result = await signupAsPro({
        email: form.email,
        password: form.password,
        firstName: form.firstName,
        lastName: form.lastName,
        phone: form.phone,
        whatsapp: form.whatsapp,
        registrationData: questions,
        tenantSubdomain,
      });
      if (result.requiresEmailConfirmation) {
        navigate('/login?confirm=1', { replace: true });
      } else {
        navigate('/pending-approval', { replace: true });
      }
    } catch (err) {
      // `pro-signup` rejette avec un code (email_already_registered, …) —
      // l'afficher brut ne dirait rien à l'électricien.
      setSubmitError(proErrorMessage(err, t));
      setStep('questions');
    }
  };

  return (
    <AuthLayout>
      {step === 'form' && (
        <>
          <h2 className="text-xl font-semibold text-neutral-900 mb-1">
            {t('pro.auth.register_title', 'Créer un compte')}
          </h2>
          <p className="text-sm text-neutral-500 mb-6">
            {t('pro.auth.register_subtitle', 'Étape 1/2 : informations personnelles')}
          </p>
          <form onSubmit={onFormNext} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Field
                label={t('pro.auth.first_name', 'Prénom')}
                value={form.firstName}
                onChange={(v) => setForm({ ...form, firstName: v })}
                error={errors['firstName']}
              />
              <Field
                label={t('pro.auth.last_name', 'Nom')}
                value={form.lastName}
                onChange={(v) => setForm({ ...form, lastName: v })}
                error={errors['lastName']}
              />
            </div>
            <Field
              label={t('pro.auth.email', 'Email')}
              type="email"
              value={form.email}
              onChange={(v) => setForm({ ...form, email: v })}
              error={errors['email']}
            />
            <Field
              label={t('pro.auth.phone', 'Téléphone')}
              type="tel"
              value={form.phone}
              onChange={(v) => setForm({ ...form, phone: v.replace(/[^0-9]/g, '') })}
              error={errors['phone']}
            />
            <Field
              label={t('pro.auth.whatsapp', 'WhatsApp (optionnel)')}
              type="tel"
              value={form.whatsapp ?? ''}
              onChange={(v) => setForm({ ...form, whatsapp: v.replace(/[^0-9+]/g, '') })}
            />
            <Field
              label={t('pro.auth.password', 'Mot de passe')}
              type="password"
              value={form.password}
              onChange={(v) => setForm({ ...form, password: v })}
              error={errors['password']}
              hint={t('pro.auth.password_hint', '{{min}} caractères minimum', {
                min: PRO_MIN_PASSWORD_LENGTH,
              })}
            />
            <button
              type="submit"
              className="w-full py-2.5 rounded-lg text-sm font-semibold text-white"
              style={{ background: 'var(--pro-primary)' }}
            >
              {t('pro.auth.continue', 'Continuer')}
            </button>
          </form>
        </>
      )}

      {step === 'questions' && (
        <>
          <h2 className="text-xl font-semibold text-neutral-900 mb-1">
            {t('pro.auth.questions_title', 'Profession')}
          </h2>
          <p className="text-sm text-neutral-500 mb-6">
            {t('pro.auth.questions_subtitle', 'Étape 2/2 : aidez-nous à mieux vous servir')}
          </p>

          <div className="space-y-3">
            <SelectField
              label={t('pro.auth.experience', "Années d'expérience")}
              value={questions.experience ?? ''}
              onChange={(v) => setQuestions({ ...questions, experience: v })}
              options={[
                { value: '', label: '—' },
                { value: '<2', label: 'Moins de 2 ans' },
                { value: '2-5', label: '2 à 5 ans' },
                { value: '5-10', label: '5 à 10 ans' },
                { value: '>10', label: 'Plus de 10 ans' },
              ]}
            />
            <SelectField
              label={t('pro.auth.work_type', 'Type de travail')}
              value={questions.workType ?? ''}
              onChange={(v) => setQuestions({ ...questions, workType: v })}
              options={[
                { value: '', label: '—' },
                { value: 'residential', label: 'Résidentiel' },
                { value: 'commercial', label: 'Commercial' },
                { value: 'industrial', label: 'Industriel' },
                { value: 'mixed', label: 'Mixte' },
              ]}
            />
            {/* Gouvernorat : liste fermée des 24 wilayas, libellées en arabe
                quand l'interface l'est. La valeur STOCKÉE reste le nom
                français, pour que la fiche lue par le fournisseur ne change
                pas de langue selon celle de l'électricien. */}
            <SelectField
              label={t('pro.auth.region', 'Gouvernorat')}
              value={questions.region ?? ''}
              onChange={(v) => setQuestions({ ...questions, region: v })}
              options={[
                {
                  value: '',
                  label: t('pro.checkout.city_select', 'Sélectionner un gouvernorat'),
                },
                ...governorates.map((g) => ({ value: g.value, label: g.label })),
              ]}
            />

            {submitError && (
              <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
                {submitError}
              </div>
            )}

            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setStep('form')}
                className="flex-1 py-2.5 rounded-lg text-sm font-medium border border-neutral-200 hover:bg-neutral-50"
              >
                {t('pro.auth.back', 'Retour')}
              </button>
              <button
                type="button"
                onClick={onFinalSubmit}
                className="flex-1 py-2.5 rounded-lg text-sm font-semibold text-white"
                style={{ background: 'var(--pro-primary)' }}
              >
                {t('pro.auth.submit', 'Créer mon compte')}
              </button>
            </div>
          </div>
        </>
      )}

      {step === 'submitting' && (
        <div className="text-center py-8">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-orange-500" />
          <p className="text-sm text-neutral-600 mt-3">
            {t('pro.auth.submitting', 'Création de votre compte…')}
          </p>
        </div>
      )}

      <p className="text-sm text-neutral-500 text-center mt-6">
        {t('pro.auth.have_account', 'Déjà inscrit ?')}{' '}
        <Link to="/login" className="text-orange-600 font-medium hover:underline">
          {t('pro.auth.login_link', 'Se connecter')}
        </Link>
      </p>
    </AuthLayout>
  );
}

function Field({
  label,
  type = 'text',
  value,
  onChange,
  error,
  hint,
}: {
  label: string;
  type?: string;
  value: string;
  onChange: (v: string) => void;
  error?: string;
  hint?: string;
}) {
  return (
    <div>
      <label className="text-sm font-medium text-neutral-700 mb-1.5 block">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full px-3 py-2.5 rounded-lg border ${
          error ? 'border-red-300' : 'border-neutral-200'
        } focus:outline-none focus:ring-2 focus:ring-orange-300`}
      />
      {hint && <p className="text-xs text-neutral-500 mt-1">{hint}</p>}
      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
    </div>
  );
}

function SelectField({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <div>
      <label className="text-sm font-medium text-neutral-700 mb-1.5 block">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2.5 rounded-lg border border-neutral-200 focus:outline-none focus:ring-2 focus:ring-orange-300"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}
